import type { Patient, Reference } from '@medplum/fhirtypes'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Participant } from '@seen/core/models/patients/participant'
import { KBD, TypographyDetail } from '@seen/ui'
import { IconArrowUp } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { z } from 'zod'
import { Card } from '#src/components/core/Card.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { RequiredAnswersDialog } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { useHeldAnswers } from '#src/components/rich-text-editor/required/useHeldAnswers.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { trimHTML } from '#src/utils/trimHTML.ts'

const formSchema = z.object({
  message: z
    .string()
    .transform(trimHTML)
    .refine((v) => v.length > 0, { message: 'Message required' }),
})

export interface CommentComposerProps {
  partOf: Reference[]
  subject?: Reference<Patient>
  placeholder?: string
  className?: string
}

export function CommentComposer({
  partOf,
  subject,
  placeholder = 'Leave a comment…',
  className,
}: CommentComposerProps): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const { data: participant } = useRead(Participant, subject)

  const storeId = useMemo(
    () =>
      JSON.stringify(
        orderBy(
          partOf.map((p) => p.reference),
          [(reference) => reference],
          ['asc']
        )
      ),
    [partOf]
  )

  const form = useForm({
    schema: formSchema,
    defaultValues: {
      message: '',
    },
    restorationId: storeId,
  })

  const createCommunication =
    useCreateMutation<CommentCommunication>().mutateAsync
  // Answers given in the comment are recorded once it is posted.
  const held = useHeldAnswers({ surface: 'comment' })

  if (isReadOnly) {
    return null
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    const prepared = await held.prepare(values.message)
    if (!prepared) {
      return
    }
    const communication = new CommentCommunication({
      message: prepared.message,
      sender: practitioner.reference,
      subject,
      partOf,
    })

    const created = await createCommunication(communication)
    form.reset({ message: '' })
    held.record(prepared.answers, {
      noteKey: created.reference.reference ?? '',
      subject: subject ?? participant?.reference,
    })
  })

  return (
    <Card className={className}>
      <Form onSubmit={handleSubmit} className="flex gap-2">
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <RichTextEditor
              aria-label="Timeline comment composer"
              placeholder={placeholder}
              variant="unstyled"
              className="-ml-3 flex-1 pl-3"
              contentClassName="text-sm pl-4 -ml-4 w-auto py-6.5 -my-4 [&_p]:leading-5"
              enablePractitionerMention
              related={partOf}
              participant={participant}
              editorRef={held.editorRef}
              observationRecordStatus="deferred"
              {...field}
            />
          )}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <FormSubmitButton
              control={form.control}
              size="icon"
              variant="outline"
              className="self-end rounded-full"
              aria-label="Submit comment"
            >
              <IconArrowUp className="size-4" />
            </FormSubmitButton>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="flex items-center gap-1 px-2"
          >
            <div className="flex gap-1">
              <KBD>⌘</KBD>
              <KBD>Enter</KBD>
            </div>
            <TypographyDetail className="text-foreground-secondary">
              to submit comment
            </TypographyDetail>
          </TooltipContent>
        </Tooltip>
      </Form>
      <RequiredAnswersDialog {...held.dialog} />
    </Card>
  )
}
